import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    input,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';

import { Subject } from 'rxjs';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { intersectionWith } from 'lodash-es';
import { DndDropEvent, DndDropzoneDirective, DndModule } from 'ngx-drag-drop';
import {
    ActionGroup,
    ShActionGroupComponent
} from '../../../../elements/components/action-group/action-group.component';
import { ShIconModule } from '../../../../elements/components/icon/icon.module';
import { LoadingBehaviorSubject } from '../../../../helpers/loading-behavior-subject.helper';
import { ShTableHeadComponent } from '../../../../overviews/generic-overview/components/table-head/table-head.component';
import { SelectionState } from '../../../generic-overview/components/context-action-bar/entities/select-mode.entity';

import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { Comparable, Trackable } from '../../../../helpers/public_api';
import { SortInfo } from '../../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { IsEventFromActionItem } from '../../../helpers/is-event-from-action-item.helper';
import { IsEventFromFormInput } from '../../../helpers/is-event-from-form-input.helper';
import { CellType } from '../../components/cells/_base/table-cell.entity';
import { ShGenericCellComponent } from '../../components/cells/_generic-cell/generic-cell.component';
import { ColumnDef } from '../../entities/column-definition.entity';
import { DragAndDropState } from '../../entities/drag-and-drop-state.entity';

@Component({
    standalone: true,
    selector: 'sh-generic-list',
    templateUrl: './generic-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        DndModule,
        ShActionGroupComponent,
        ShGenericCellComponent,
        ShTableHeadComponent,
        TranslateModule,
        ShIconModule,
        DndDropzoneDirective
    ]
})
export class ShGenericListComponent<T extends Comparable & Trackable> implements OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    public loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    private draggedItem: T | null = null;

    columns = input<ColumnDef<any, T>[]>([]);
    columnCount = computed(() =>
        this.getColumnCount(this.columns(), this.availableSelect(), this.availableDragAndDrop())
    );

    columnOptions = input<FormSelectOption[]>([]);
    @Output() columnSelectionChanged = new EventEmitter<string[]>();

    // #region List elements
    @Input() autoRefresh: boolean = false; // MED-6487: No deferring when autorefreshing to prevent flickering
    @Input() rowElements: T[] = [];

    @Input() loadingMsg = '';
    @Input() loadingFailedMsg = '';
    @Input() emptyListMsg = '';

    @Input() set isLoading(loading: boolean) {
        this.loading$.next(loading);
    }
    // #endregion

    // #region Selection
    availableSelect = input<boolean>(false);
    @Input() disableSelect = false;
    @Input() canBatchSelect = false;
    @Input() disableSelectMessage = '';

    @Input() isInitialBulk: boolean;

    @Input() selectedElements: T[] = [];
    @Input() selectedItemsCount: number;

    @Input() itemsSelectedLabelKey?: string;
    @Input() noItemsSelectedLabelKey?: string;
    @Input() hideColumnHeaders: boolean;
    @Input() dragAndDropTooltip: string = '';

    @Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();
    @Output() itemDeselected: EventEmitter<T> = new EventEmitter<T>();

    @Output() selectionChanged: EventEmitter<SelectionState> = new EventEmitter<SelectionState>();
    // #endregion

    // #region Sort
    allowSort = input<boolean>(true);
    @Input() sortInfo: SortInfo;
    @Output() sortInfoChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();
    // #endregion

    // #region Drag and drop
    availableDragAndDrop = input<boolean>(false);
    @Input() disableDragAndDrop = false;
    @Output() itemsOrderChanged: EventEmitter<DragAndDropState<T>> = new EventEmitter<DragAndDropState<T>>();
    // #endregion

    // #region Item actions
    @Input() actionItems?: ActionGroup[];
    @Input() contextActionGroup?: ActionGroup;
    @Input() stickyActions: boolean = true;
    // #endregion

    // #region Active items
    @Input() activeItem: T | null;
    @Input() highlightedItem: T | null;
    // #endregion

    // #region Error handling
    @Input() hasError = false;
    // #endregion

    // #region Click event propagation
    @Input() areItemsClickable: boolean = false;
    @Output() itemClicked: EventEmitter<T> = new EventEmitter<T>();
    @Output() cellClicked: EventEmitter<{ cellType: CellType; item: T }> = new EventEmitter<{
        cellType: CellType;
        item: T;
    }>();
    // #endregion

    @ViewChild('tbody') dropContainer: ElementRef<HTMLElement>;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onRowClicked(event: Event, item: T): void {
        if (IsEventFromActionItem(event) || IsEventFromFormInput(event)) {
            return;
        }

        this.itemClicked.emit(item);
    }

    public onSelectRow(event: Event, item: T): void {
        const clickedElement: HTMLInputElement | null = event.target as HTMLInputElement;
        const isChecked: boolean = clickedElement ? clickedElement.checked : false;

        if (isChecked) {
            this.itemSelected.emit(item);
        } else {
            this.itemDeselected.emit(item);
        }
    }

    public isRowActive(item: T): boolean {
        return !!this.activeItem && this.activeItem.equals(item);
    }

    public isRowHighlighted(item: T): boolean {
        return !!this.highlightedItem && this.highlightedItem.equals(item);
    }

    public isRowSelected(item: T): boolean {
        return (
            this.availableSelect() &&
            !!this.selectedElements &&
            this.selectedElements.findIndex(selected => item.equals(selected)) > -1
        );
    }

    private getColumnCount(
        columns: ColumnDef<any, T>[],
        availableSelect: boolean,
        availableDragAndDrop: boolean
    ): number {
        const numberOfColumns: number = columns.length + 1;
        const multiSelectFormAddition: number = availableSelect ? 1 : 0;
        const dragAndDropListAddition: number = availableDragAndDrop ? 1 : 0;

        return numberOfColumns + multiSelectFormAddition + dragAndDropListAddition;
    }

    public trackByFunction(index: number, item: T): any {
        return item.trackBy();
    }

    public onCellEvent(cellType: CellType, item: T) {
        this.cellClicked.emit({ cellType, item });
    }

    public get selectedPageItems(): number {
        return intersectionWith(this.selectedElements, this.rowElements, (x, y) => x.equals(y))?.length;
    }

    public onDragStart(rowElement: T) {
        this.draggedItem = rowElement;

        this.hideDraggedElement();
    }

    // Hide element that is being dragged to avoid flickering
    // This has to be done on a timeout to avoid the element being hidden before the drag event is triggered
    private hideDraggedElement() {
        setTimeout(() => {
            const draggedElement = document
                .getElementsByClassName('sh-card-table__row--dragging')
                .item(0) as HTMLElement;

            if (draggedElement) {
                draggedElement.style.display = 'none';
            }
        }, 10);
    }

    // Will trigger first and adds element to the list where it was dropped
    public onDrop(event: DndDropEvent) {
        if (this.draggedItem && event.dropEffect === 'move') {
            // Remove old item from list
            const oldIndex = this.rowElements.indexOf(this.draggedItem);
            this.rowElements.splice(oldIndex, 1);

            // Insert item to list at new index
            let newIndex = event.index;

            if (typeof newIndex === 'undefined') {
                newIndex = this.rowElements.length;
            }

            if (oldIndex < newIndex) {
                // If we move the item down the list, we need to subtract 1 from the index to take into account the old item being removed from the list
                newIndex = newIndex - 1;
            }

            this.rowElements.splice(newIndex, 0, this.draggedItem);
        }

        const result: DragAndDropState<T> = {
            draggedItem: this.draggedItem,
            itemsInOrder: this.rowElements
        };

        this.draggedItem = null;

        this.unhideDraggedElement();

        this.itemsOrderChanged.emit(result);
    }

    private unhideDraggedElement() {
        const draggedElements = document.getElementsByTagName('tr');

        for (let i = 0; i < draggedElements.length; i++) {
            const draggedElement = draggedElements.item(i) as HTMLElement;
            if (draggedElement) {
                draggedElement.style.display = 'table-row';
            }
        }
    }

    public onDragCanceled() {
        this.draggedItem = null;

        this.unhideDraggedElement();
    }

    public onSelectionChanged(selectionState: SelectionState): void {
        this.selectionChanged.emit(selectionState);
    }

    public onColumnSelectionChanged(selectedColumns: string[]): void {
        this.columnSelectionChanged.emit(selectedColumns);
    }

    public onSortInfoChanged(sortInfo: SortInfo): void {
        this.sortInfoChanged.emit(sortInfo);
    }

    // #region drag and drop hacks
    public onDrag(event: DragEvent): void {
        const tbody: HTMLElement = this.dropContainer.nativeElement;

        // Event is captured on the table, we'll modify the event so that it's captured on the tbody instead
        if (event.relatedTarget && (event.relatedTarget as HTMLElement).tagName === tbody.parentElement?.tagName) {
            // Prevent event from bubbling, since we'll be sending our own event
            event.stopPropagation();

            const target: HTMLElement | null = event.target as HTMLElement | null;
            if (tbody && target) {
                const newEvent = new DragEvent(event.type);

                for (let prop in event) {
                    let d: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(event, prop);
                    if (d) {
                        Object.defineProperty(newEvent, prop, d);
                    }
                }

                // Manually set the relatedTarget property to tbody
                Object.defineProperty(newEvent, 'relatedTarget', {
                    value: tbody,
                    writable: false
                });

                // Dispatch the new event on the tbody
                tbody?.dispatchEvent(newEvent);
            }
        }
    }

    // Dropevent doesn't trigger on tbody. Capture it on parent instead and send it to the tbody
    public onParentDrop(event: DragEvent) {
        const tbody = this.dropContainer.nativeElement;

        // A copy is needed since you can't re-send events
        const newEvent = new DragEvent(event.type);

        for (let prop in event) {
            let d: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(event, prop);
            if (d) {
                Object.defineProperty(newEvent, prop, d);
            }
        }

        tbody?.dispatchEvent(newEvent);
    }

    public onParentDragOver(event: DragEvent): void {
        // Drop event only triggers if these are disabled
        event.preventDefault();
        event.stopPropagation();
    }

    // Need to capture drag leave on the parent to properly handle cleanup after a cancel
    public onParentDragLeave(event: DragEvent): void {
        const tbody = this.dropContainer.nativeElement;

        // A copy is needed since you can't re-send events
        const newEvent = new DragEvent(event.type);

        for (let prop in event) {
            let d: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(event, prop);
            if (d) {
                Object.defineProperty(newEvent, prop, d);
            }
        }

        tbody?.dispatchEvent(newEvent);
    }

    // #endregion
}
